import { useState } from 'react';
import { TextField, Autocomplete, Box, Paper } from '@mui/material';
import { Phone as PhoneIcon } from '@mui/icons-material';
import { useBusiness } from "hooks/useBusiness";
import { useLocalization } from "hooks/useLocalization";
import { CountryOption } from "interfaces/BusinessInterfaces";

interface PhoneFormFieldProps {
	countryCode: string;
	onCountryCodeChange: (newCountryCode: string) => void;
	phone: string;
	onPhoneChange: (newPhone: string) => void;
	disabled: boolean;
}

function PhoneFormField({ countryCode, onCountryCodeChange, phone, onPhoneChange, disabled }: PhoneFormFieldProps) {
	const { countryOptions } = useBusiness();
	const { getStr } = useLocalization();
	const [countryObj, setCountryObj] = useState<CountryOption | undefined>(countryOptions.find(c => c.code === countryCode));
    const [touched, setTouched] = useState(false);

	const handleCountryObjChange = (event: any, newCountry: CountryOption | null) => {
		if (newCountry) {
			setCountryObj(newCountry);
			onCountryCodeChange(newCountry.code);
		}
	}

	const handlePhoneChangeFiltered = (newPhone: string) => {
		// Remove any characters that are not digits, spaces, hyphens, parentheses, or dots
		let filteredPhone = newPhone.replace(/[^0-9 \-().]/g, '');

		if (filteredPhone !== newPhone) {
			return;
		}

		onPhoneChange(filteredPhone);
	};

	const handleBlur = () => {
        if (!touched) setTouched(true);  // Set touched state when the field is blurred
    };

	const filterOptions = (options: CountryOption[], { inputValue }: { inputValue: string }) => {
		return options.filter(
			option => option.title.toLowerCase().includes(inputValue.toLowerCase()) ||
				('+' + option.phoneCode).includes(inputValue)
		);
	};

	return (
		<Box display="flex" alignItems="center">
			<PhoneIcon sx={{ mr: 1 }} />
			<Autocomplete
				value={countryObj}
				onChange={handleCountryObjChange}
				id="country-code"
				sx={{ width: '105px', marginRight: '12px' }}
				options={countryOptions}
				autoHighlight
				disableClearable
				filterOptions={filterOptions}
				getOptionLabel={(option) => '+' + option.phoneCode}
				disabled={disabled}
				renderOption={(props, option) => (
					<Box component="li" {...props} key={option.code}>
						<img
							alt={option.code}
							src={`https://www.tourtask.com/images/icons/flags/countries/flag-${option.code.toLowerCase()}-30x20.png`}
							style={{ width: 30, height: 20, marginRight: 12 }}
						/>
						<span style={{ width: '50px' }}>+{option.phoneCode}</span>
						<span>{option.title}</span>
					</Box>
				)}
				renderInput={(params) => (
					<TextField
						{...params}
						label={getStr('Country')}
						required
						inputProps={{
							...params.inputProps,
							autoComplete: 'new-password', // disable autocomplete and autofill
						}}
					/>
				)}
				PaperComponent={({ children }) => (
					<Paper style={{ minWidth: 300 }} elevation={3}>{children}</Paper>
				)}
			/>
			<TextField
				label={'Phone'}
				variant="outlined"
				value={phone}
                onBlur={handleBlur}
				required
				sx={{ flexGrow: 1 }}
				onChange={(e) => handlePhoneChangeFiltered(e.target.value)}
				error={touched && phone.length === 0}
				disabled={disabled}
			/>
		</Box>
	);
}

export default PhoneFormField;
